import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Lock, MagnifyingGlass, Globe, Medal, ShareNetwork, DownloadSimple, Link as LinkIcon, WhatsappLogo } from '@phosphor-icons/react';
import PrizeBanner from '../components/PrizeBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONFEDERATIONS = ['All', 'UEFA', 'CONMEBOL', 'CONCACAF', 'CAF', 'AFC', 'OFC'];

const POSITION_LABELS = {
  winner_team_id: 'winner',
  runner_up_team_id: 'runnerUp',
  third_place_team_id: 'thirdPlace',
  fourth_place_team_id: 'fourthPlace',
};

const TeamSelector = ({ teams, selectedId, onSelect, label, disabledIds, search, setSearch, confFilter, setConfFilter, t }) => {
  const filtered = useMemo(() => {
    let list = teams;
    if (confFilter !== 'All') list = list.filter(t => t.confederation === confFilter);
    if (search) list = list.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
    return list;
  }, [teams, confFilter, search]);

  return (
    <div className="bg-background-paper border border-border rounded-sm p-4" data-testid={`wc-selector-${label}`}>
      <label className="block text-sm uppercase tracking-wide text-[#FFD700] font-bold mb-3">{t(`worldCup.${label}`)}</label>

      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('worldCup.searchTeams')}
            className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-sm text-white text-sm placeholder:text-text-muted focus:border-primary outline-none"
            data-testid={`wc-search-${label}`}
          />
        </div>
        <select
          value={confFilter}
          onChange={e => setConfFilter(e.target.value)}
          className="bg-background border border-border rounded-sm text-white text-sm px-2 py-2 focus:border-primary outline-none"
          data-testid={`wc-conf-filter-${label}`}
        >
          {CONFEDERATIONS.map(c => <option key={c} value={c}>{c === 'All' ? t('worldCup.allTeams') : c}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
        {filtered.map(team => {
          const isSelected = team.id === selectedId;
          const isDisabled = disabledIds.includes(team.id) && !isSelected;
          return (
            <button
              key={team.id}
              onClick={() => !isDisabled && onSelect(team.id)}
              disabled={isDisabled}
              data-testid={`wc-team-${team.id}`}
              className={`flex items-center gap-2 p-2 rounded-sm text-left text-sm transition-all ${
                isSelected
                  ? 'bg-primary text-black font-bold border border-primary'
                  : isDisabled
                    ? 'bg-background-subtle/50 text-text-muted cursor-not-allowed opacity-40'
                    : 'bg-background-subtle border border-border hover:border-primary/50 text-white hover:bg-background-subtle/80'
              }`}
            >
              {team.flag && <img src={team.flag} alt="" className="w-6 h-4 object-cover rounded-sm flex-shrink-0" />}
              <span className="truncate">{team.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default function WorldCupPage() {
  const { t } = useTranslation();
  const [teams, setTeams] = useState([]);
  const [status, setStatus] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);

  const [form, setForm] = useState({
    winner_team_id: '',
    runner_up_team_id: '',
    third_place_team_id: '',
    fourth_place_team_id: '',
    total_goals: 150,
  });

  // Search/filter states per selector
  const [searches, setSearches] = useState({ winner: '', runnerUp: '', thirdPlace: '', fourthPlace: '' });
  const [confFilters, setConfFilters] = useState({ winner: 'All', runnerUp: 'All', thirdPlace: 'All', fourthPlace: 'All' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, statusRes, predRes, lbRes] = await Promise.all([
        axios.get(`${API}/wc/teams`),
        axios.get(`${API}/wc/status`),
        axios.get(`${API}/wc/prediction`),
        axios.get(`${API}/wc/leaderboard`),
      ]);
      setTeams(teamsRes.data);
      setStatus(statusRes.data);
      setLeaderboardData(lbRes.data);
      if (predRes.data) {
        setPrediction(predRes.data);
        setForm({
          winner_team_id: predRes.data.winner_team_id,
          runner_up_team_id: predRes.data.runner_up_team_id,
          third_place_team_id: predRes.data.third_place_team_id,
          fourth_place_team_id: predRes.data.fourth_place_team_id,
          total_goals: predRes.data.total_goals,
        });
      }
    } catch (err) {
      console.error('Failed to fetch WC data:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedIds = [form.winner_team_id, form.runner_up_team_id, form.third_place_team_id, form.fourth_place_team_id].filter(Boolean);

  const handleSubmit = async () => {
    if (!form.winner_team_id || !form.runner_up_team_id || !form.third_place_team_id || !form.fourth_place_team_id) {
      toast.error('Please select all 4 positions');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/wc/prediction`, form);
      toast.success(prediction ? 'Prediction updated!' : 'Prediction submitted!');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit prediction');
    } finally {
      setSubmitting(false);
    }
  };

  const getTeamName = (id) => teams.find(t => t.id === id)?.name || '-';
  const getTeamFlag = (id) => teams.find(t => t.id === id)?.flag || null;

  const handleShare = async () => {
    setShareLoading(true);
    try {
      const res = await axios.post(`${API}/wc/share`);
      const token = res.data.share_token;
      setShareToken(token);
      const shareUrl = `${window.location.origin}/share/${token}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Share link copied to clipboard!');
      } catch {
        toast.success('Share link ready! Click "View & Download Card" to open it.');
      }
    } catch (err) {
      toast.error('Failed to generate share link');
    } finally {
      setShareLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-text-secondary">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const isLocked = status?.locked;

  return (
    <div className="min-h-screen bg-background" data-testid="world-cup-page">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Globe size={32} weight="bold" className="text-[#FFD700]" />
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white" data-testid="wc-title">
              {t('worldCup.title')}
            </h1>
          </div>
          <p className="text-text-secondary text-base">{t('worldCup.subtitle')}</p>
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <span className="text-xs uppercase tracking-wide text-text-muted">
              {t('worldCup.deadline')}: <span className="text-primary font-mono font-bold">June 10, 2026 BST</span>
            </span>
            <span className="text-xs uppercase tracking-wide text-text-muted">
              {t('worldCup.predictionsCount')}: <span className="text-white font-mono font-bold">{status?.total_predictions || 0}</span>
            </span>
          </div>
        </div>

        {/* Prize Banner */}
        <div className="mb-6">
          <PrizeBanner variant="compact" />
        </div>

        {/* Locked Banner */}
        {isLocked && (
          <div className="bg-accent/10 border border-accent/30 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="wc-locked-banner">
            <Lock size={24} className="text-accent flex-shrink-0" />
            <div>
              <p className="text-accent font-bold">{t('worldCup.locked')}</p>
              <p className="text-text-secondary text-sm">{t('worldCup.lockedDesc')}</p>
            </div>
          </div>
        )}

        {/* Current Prediction Display */}
        {prediction && (
          <div className="bg-background-paper border border-[#FFD700]/30 rounded-sm p-5 mb-6" data-testid="wc-current-prediction">
            <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy size={22} weight="bold" className="text-[#FFD700]" />
              {t('worldCup.yourPrediction')}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(POSITION_LABELS).map(([key, label]) => (
                <div key={key} className="bg-background-subtle border border-border rounded-sm p-3 text-center">
                  <p className="text-xs uppercase tracking-wide text-[#FFD700] mb-2 font-bold">{t(`worldCup.${label}`)}</p>
                  {getTeamFlag(prediction[key]) && (
                    <img src={getTeamFlag(prediction[key])} alt="" className="w-10 h-6 object-cover rounded-sm mx-auto mb-1" />
                  )}
                  <p className="text-white text-sm font-medium">{getTeamName(prediction[key])}</p>
                </div>
              ))}
            </div>
            <div className="mt-3 text-center">
              <span className="text-xs uppercase tracking-wide text-text-muted">{t('worldCup.totalGoals')}: </span>
              <span className="font-mono font-bold text-primary text-lg">{prediction.total_goals}</span>
            </div>
            {/* Share Buttons */}
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <button
                onClick={handleShare}
                disabled={shareLoading}
                data-testid="wc-share-link-btn"
                className="flex items-center gap-2 px-4 py-2 bg-[#FFD700]/20 border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/30 rounded-sm transition-all text-sm font-bold uppercase tracking-wide"
              >
                <LinkIcon size={18} weight="bold" />
                {shareLoading ? '...' : (shareToken ? t('share.linkCopied') : t('share.shareLink'))}
              </button>
              {shareToken && (
                <a
                  href={`/share/${shareToken}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wc-view-share-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 text-white hover:bg-white/20 rounded-sm transition-all text-sm font-bold uppercase tracking-wide"
                >
                  <ShareNetwork size={18} weight="bold" />
                  {t('share.viewDownload')}
                </a>
              )}
              {shareToken && (
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Check out my World Cup 2026 prediction! Can you beat it? 🏆⚽\n${window.location.origin}/share/${shareToken}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="wc-whatsapp-share-btn"
                  className="flex items-center gap-2 px-4 py-2 bg-[#25D366]/20 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/30 rounded-sm transition-all text-sm font-bold uppercase tracking-wide"
                >
                  <WhatsappLogo size={18} weight="bold" />
                  {t('share.whatsapp')}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Prediction Form */}
        {!isLocked && (
          <div className="space-y-4 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(POSITION_LABELS).map(([key, label]) => (
                <TeamSelector
                  key={key}
                  teams={teams}
                  selectedId={form[key]}
                  onSelect={(id) => setForm(prev => ({ ...prev, [key]: id }))}
                  label={label}
                  disabledIds={selectedIds.filter(s => s !== form[key])}
                  search={searches[label]}
                  setSearch={(v) => setSearches(prev => ({ ...prev, [label]: v }))}
                  confFilter={confFilters[label]}
                  setConfFilter={(v) => setConfFilters(prev => ({ ...prev, [label]: v }))}
                  t={t}
                />
              ))}
            </div>

            {/* Total Goals */}
            <div className="bg-background-paper border border-border rounded-sm p-4" data-testid="wc-total-goals-section">
              <label className="block text-sm uppercase tracking-wide text-[#FFD700] font-bold mb-2">{t('worldCup.totalGoals')}</label>
              <p className="text-text-secondary text-xs mb-3">{t('worldCup.totalGoalsDesc')}</p>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  min={0}
                  max={500}
                  value={form.total_goals}
                  onChange={e => setForm(prev => ({ ...prev, total_goals: parseInt(e.target.value) || 0 }))}
                  className="w-32 px-4 py-3 bg-background border border-border rounded-sm text-white font-mono text-xl text-center focus:border-primary outline-none"
                  data-testid="wc-total-goals-input"
                />
                <input
                  type="range"
                  min={50}
                  max={500}
                  value={form.total_goals}
                  onChange={e => setForm(prev => ({ ...prev, total_goals: parseInt(e.target.value) }))}
                  className="flex-1 accent-primary h-2"
                  data-testid="wc-total-goals-slider"
                />
              </div>
            </div>

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="wc-submit-btn"
              className="w-full py-4 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide text-lg rounded-sm transition-all hover:scale-[1.01] glow-effect disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? t('common.loading') : (prediction ? t('worldCup.update') : t('worldCup.submit'))}
            </button>
          </div>
        )}

        {/* Leaderboard */}
        <div className="bg-background-paper border border-border rounded-sm p-5" data-testid="wc-leaderboard-section">
          <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Medal size={22} weight="bold" className="text-[#FFD700]" />
            {t('worldCup.leaderboard')}
          </h2>

          {!leaderboardData?.results_entered ? (
            <p className="text-text-secondary text-center py-8">{t('worldCup.noResults')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="wc-leaderboard-table">
                <thead>
                  <tr className="border-b border-border text-text-muted uppercase tracking-wide text-xs">
                    <th className="py-2 px-3 text-left">#</th>
                    <th className="py-2 px-3 text-left">{t('common.player')}</th>
                    <th className="py-2 px-3 text-center">{t('worldCup.correctPositions')}</th>
                    <th className="py-2 px-3 text-center">{t('worldCup.goalsDiff')}</th>
                    <th className="py-2 px-3 text-left">{t('worldCup.winner')}</th>
                    <th className="py-2 px-3 text-left">{t('worldCup.runnerUp')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardData.leaderboard.map((entry, i) => (
                    <tr key={entry.user_id} className="border-b border-border/50 hover:bg-white/5">
                      <td className="py-3 px-3 font-mono font-bold">
                        {i === 0 ? <span className="text-[#FFD700]">1</span> : i === 1 ? <span className="text-secondary">2</span> : i === 2 ? <span className="text-accent">3</span> : <span className="text-text-muted">{i + 1}</span>}
                      </td>
                      <td className="py-3 px-3 text-white">{entry.display_name || entry.email?.split('@')[0]}</td>
                      <td className="py-3 px-3 text-center font-mono text-primary font-bold">{entry.correct_positions}/4</td>
                      <td className="py-3 px-3 text-center font-mono text-text-secondary">{entry.goals_diff}</td>
                      <td className="py-3 px-3 flex items-center gap-1.5">
                        {getTeamFlag(entry.winner) && <img src={getTeamFlag(entry.winner)} alt="" className="w-5 h-3 object-cover rounded-sm" />}
                        <span className="text-white text-xs">{getTeamName(entry.winner)}</span>
                      </td>
                      <td className="py-3 px-3">
                        <span className="text-text-secondary text-xs">{getTeamName(entry.runner_up)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
